import {MeterAssignment} from '../types';

// channelIdentifiers is an AWSJSON scalar on the wire — AppSync expects a
// JSON-encoded STRING, not an object. The edit form holds it as either a parsed
// object (round-tripped from a read) or a JSON text string (typed into the
// textarea). normalizeChannelIdentifiers coerces both to the canonical JSON
// string form the mutation variables require. Invalid JSON throws so the caller
// (mutation) surfaces the error rather than silently sending garbage.
export const normalizeChannelIdentifiers = (value: unknown): string => {
    if (typeof value === 'string') {
        // Validate it parses; re-stringify to canonicalize whitespace.
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed);
    }
    if (value && typeof value === 'object') {
        return JSON.stringify(value);
    }
    // Treat empty/undefined as an empty object.
    return JSON.stringify({});
};

// Builds the variables payload for the createMeterAssignment mutation.
// Whitelists the fields declared on backend CreateMeterAssignmentInput
// (meter-assignments.graphql) so frontend-only fields (createdBy) and
// server-managed fields (id, sortKey, updated, createdByUser) cannot leak into
// the mutation variables and trigger AppSync's "field not defined for input
// object type" validation error (FNP-33 pattern).
// FNP-217: sortKey is server-derived (= meterId#channel) and MUST NOT be sent.
export const buildCreateMeterAssignmentInput = (values: MeterAssignment): Record<string, unknown> => {
    const input: Record<string, unknown> = {
        customerId: values.customerId,
        meterId: values.meterId,
        channel: values.channel,
        channelIdentifiers: normalizeChannelIdentifiers(values.channelIdentifiers),
        status: values.status,
        effectiveFrom: values.effectiveFrom,
    };
    if (values.freeTierQuantity !== undefined && values.freeTierQuantity !== null) {
        input.freeTierQuantity = values.freeTierQuantity;
    }
    if (values.committedQuantity !== undefined && values.committedQuantity !== null) {
        input.committedQuantity = values.committedQuantity;
    }
    if (values.effectiveTo !== undefined && values.effectiveTo !== '') {
        input.effectiveTo = values.effectiveTo;
    }
    if (values.createdByUsername !== undefined) input.createdByUsername = values.createdByUsername;
    if (values.created !== undefined) input.created = values.created;
    return input;
};

// Builds the variables payload for the updateMeterAssignment mutation.
// Whitelists the fields declared on backend UpdateMeterAssignmentInput; includes
// id (required) and updated. sortKey is server-derived and excluded.
export const buildUpdateMeterAssignmentInput = (values: MeterAssignment): Record<string, unknown> => {
    const input: Record<string, unknown> = {
        id: values.id,
        customerId: values.customerId,
        meterId: values.meterId,
        channel: values.channel,
        channelIdentifiers: normalizeChannelIdentifiers(values.channelIdentifiers),
        status: values.status,
        effectiveFrom: values.effectiveFrom,
    };
    if (values.freeTierQuantity !== undefined && values.freeTierQuantity !== null) {
        input.freeTierQuantity = values.freeTierQuantity;
    }
    if (values.committedQuantity !== undefined && values.committedQuantity !== null) {
        input.committedQuantity = values.committedQuantity;
    }
    if (values.effectiveTo !== undefined && values.effectiveTo !== '') {
        input.effectiveTo = values.effectiveTo;
    }
    if (values.updated !== undefined) input.updated = values.updated;
    return input;
};

// Builds the variables payload for the deleteMeterAssignment mutation. FNP-217:
// the table is composite-key, so delete is by PK customerId + SK sortKey, NOT id.
export const buildDeleteMeterAssignmentInput = (values: Pick<MeterAssignment, 'customerId' | 'sortKey'>): Record<string, unknown> => ({
    customerId: values.customerId,
    sortKey: values.sortKey,
});
